import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * The Countdown app's SITE-WIDE BAR (feature 0041 V2 — the GSC-style announcement bar).
 *
 * One horizontal strip carrying the same campaign material as the product-page banner (title, subtitle,
 * digits, optional real-visitor pill) plus a call to action, rendered at the top or the bottom of EVERY
 * public page the seller targeted. It shares `_shared/countdownEngine` with the banner — one parse, one
 * clock, one preset table — so a store showing both never displays two disagreeing countdowns.
 *
 * Placement rules (the layout applies them, this component only declares them):
 *  - top + sticky   → `position: sticky; top: 0` from the very first element of the body: it stays visible
 *    while scrolling and, being IN FLOW, never covers the store header;
 *  - bottom + sticky → `position: fixed; bottom: 0` at z-index 45 — deliberately BELOW the mobile sticky
 *    buy bar (50) so an urgency strip can never sit on top of the buy button it exists to feed;
 *  - static → plain flow, first or last element of the page.
 *
 * Dismissal is per visit and per campaign (`sessionStorage`): a bar the visitor closed must not reappear
 * on every navigation of the same session, and must come back on the next one (the campaign is still live).
 */
import { useEffect, useState } from 'react';
import { t } from '@aquilixshop/i18n';
import { digitScale, floatingDigitTile, floatingPaletteFor, pad2, parseProductCountdown, scaled, splitUnits, textScale, unitLabels, useCountdownTick, } from './countdownEngine.js';
/**
 * Defensive wire → {@link CountdownBarPayload}. Same posture as the banner's parse plus the bar block;
 * the CTA is kept ONLY when both a label and a safe href survive (the backend already sanitises the href —
 * this is the second lock, because the value lands in a public `<a href>`).
 */
export function parseCountdownBar(raw) {
    const base = parseProductCountdown(raw);
    if (!base)
        return undefined;
    const r = raw;
    const barRaw = (typeof r.bar === 'object' && r.bar !== null ? r.bar : {});
    const ctaLabel = typeof barRaw.ctaLabel === 'string' ? barRaw.ctaLabel.trim() : '';
    const rawUrl = typeof barRaw.ctaUrl === 'string' ? barRaw.ctaUrl.trim() : '';
    // Relative path or http(s) only — `javascript:`, `data:` and protocol-relative `//host` are dropped,
    // never repaired (same rule as the backend's `safeHref`; this is the second lock, not the first).
    const safeUrl = rawUrl.startsWith('//')
        ? ''
        : rawUrl.startsWith('/')
            ? rawUrl
            : /^https?:\/\//i.test(rawUrl)
                ? rawUrl
                : '';
    const cta = ctaLabel !== '' && safeUrl !== '' ? { ctaLabel, ctaUrl: safeUrl } : {};
    return {
        ...base,
        bar: {
            position: barRaw.position === 'bottom' ? 'bottom' : 'top',
            sticky: barRaw.sticky !== false,
            ...cta,
        },
    };
}
/* ---------------------------------------------------------------- dismissal */
const DISMISS_PREFIX = 'aqx-cdbar-closed-';
function wasDismissed(campaignId) {
    try {
        return window.sessionStorage.getItem(DISMISS_PREFIX + campaignId) === '1';
    }
    catch {
        return false;
    }
}
function rememberDismissed(campaignId) {
    try {
        window.sessionStorage.setItem(DISMISS_PREFIX + campaignId, '1');
    }
    catch {
        /* private browsing — the bar simply reappears on the next page, which is honest enough */
    }
}
/* ---------------------------------------------------------------- component */
export function CountdownBar({ bar, locale, 
/** Close button — on by default; the dashboard preview passes false so the seller can't lose it. */
dismissible = true, }) {
    // `t({ locale }, …)` rather than the page's dictionary: these are `client:load` islands, so they render
    // once on the server and once in the browser, and only the bundled dictionary is guaranteed identical on
    // both passes. Seller overrides will reach them through a prop when lot 3 ships them.
    const { remaining, hidden } = useCountdownTick(bar);
    // The FIRST render must not read storage (SSR has none, and the hydration pass must match it), so the
    // bar always paints once; this effect closes it again immediately when the visitor dismissed it earlier
    // in the session. A one-frame flash is the price of a hydration that can never mismatch.
    const [closed, setClosed] = useState(false);
    useEffect(() => {
        if (wasDismissed(bar.campaignId))
            setClosed(true);
    }, [bar.campaignId]);
    if (hidden || closed)
        return null;
    // A strip floats over the page: transparent presets become ink, and their colours are repaired for it.
    const palette = floatingPaletteFor(bar);
    const units = splitUnits(remaining);
    const labels = unitLabels(locale);
    const cells = [
        { label: labels[0], value: units.days },
        { label: labels[1], value: units.hours },
        { label: labels[2], value: units.minutes },
        { label: labels[3], value: units.seconds },
    ];
    const onDark = palette.dark;
    const background = palette.background;
    const text = palette.text;
    const type = textScale(bar.design, { size: 13, weight: 700, subtitle: 11 });
    // The clock's own size (04/10/2026) — the strip has always painted 18px digits over an 8px unit label.
    const clock = digitScale(bar.design, 18);
    // A plate under the digits only when the seller picked one — see `floatingDigitTile`.
    const tile = floatingDigitTile(bar.design, clock.scale);
    const digitCell = {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        lineHeight: 1.05,
    };
    return (_jsxs("div", { "data-aqx-countdown-bar": bar.campaignId, "data-position": bar.bar.position, role: "region", "aria-label": bar.title ?? t({ locale }, 'a11y.countdown'), style: {
            background,
            color: text,
            padding: '10px 44px 10px 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 18,
            flexWrap: 'wrap',
            position: 'relative',
            fontSize: 13,
        }, children: [bar.title || bar.subtitle ? (_jsxs("div", { style: { textAlign: 'center' }, children: [bar.title ? (_jsx("div", { style: { fontSize: type.size, fontWeight: type.weight, lineHeight: 1.25 }, children: bar.title })) : null, bar.subtitle ? (_jsx("div", { style: { fontSize: type.subtitle, opacity: 0.72 }, children: bar.subtitle })) : null] })) : null, _jsx("div", { dir: "ltr", style: { display: 'flex', alignItems: 'center', gap: scaled(8, clock.scale) }, children: cells.map((cell, i) => (_jsxs("div", { style: { display: 'flex', alignItems: 'center', gap: scaled(8, clock.scale) }, children: [i > 0 ? (_jsx("span", { style: {
                                fontSize: scaled(13, clock.scale),
                                fontWeight: 700,
                                color: palette.digit,
                                opacity: 0.85,
                            }, children: ":" })) : null, _jsxs("span", { style: digitCell, children: [_jsx("span", { "data-aqx-cd-digit": "", style: {
                                        fontSize: clock.size,
                                        fontWeight: 700,
                                        color: palette.digit,
                                        fontVariantNumeric: 'tabular-nums',
                                        ...tile,
                                    }, children: pad2(cell.value) }), _jsx("span", { style: { fontSize: scaled(8, clock.scale), letterSpacing: 1, opacity: 0.55 }, children: cell.label })] })] }, cell.label))) }), bar.visitors ? (_jsxs("span", { style: {
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    background: onDark ? 'rgba(255,255,255,.14)' : 'rgba(0,0,0,.07)',
                    borderRadius: 999,
                    padding: '2px 9px',
                }, children: [_jsx("span", { style: { width: 6, height: 6, borderRadius: 3, background: '#34d17b' } }), _jsx("span", { style: { fontSize: 12, fontWeight: 700 }, children: bar.visitors.count }), _jsx("span", { style: { fontSize: 11, opacity: 0.8 }, children: bar.visitors.message })] })) : null, bar.bar.ctaLabel && bar.bar.ctaUrl ? (_jsx("a", { href: bar.bar.ctaUrl, style: {
                    background: palette.button,
                    color: palette.buttonText,
                    fontSize: 12,
                    fontWeight: 700,
                    padding: '7px 14px',
                    borderRadius: 6,
                    textDecoration: 'none',
                    whiteSpace: 'nowrap',
                }, children: bar.bar.ctaLabel })) : null, dismissible ? (_jsx("button", { type: "button", "aria-label": t({ locale }, 'a11y.close'), onClick: () => {
                    rememberDismissed(bar.campaignId);
                    setClosed(true);
                }, style: {
                    position: 'absolute',
                    insetInlineEnd: 12,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'transparent',
                    border: 0,
                    color: text,
                    opacity: 0.5,
                    fontSize: 16,
                    lineHeight: 1,
                    cursor: 'pointer',
                    padding: 4,
                }, children: "\u00D7" })) : null] }));
}
//# sourceMappingURL=CountdownBar.js.map